import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import {Check, Download, Instagram, Link2, Loader2, X} from 'lucide-react'
import toast from 'react-hot-toast'
import { lumi } from '@/lib/lumi'
import {
  canvasToPngBlob,
  createArticlePosterCanvas,
  downloadCanvasPng,
  downloadPngBlob,
  drawArticlePoster,
  excerptFromArticle,
  slugifyFilename,
} from '@/lib/articlePoster'
import type { NewsArticle, SiteSetting } from '@/types'

const SITE_URL = 'https://carlosfelipeofera.com'

interface InstagramPostMakerProps {
  article: NewsArticle
  open?: boolean
  onClose?: () => void
  triggerClassName?: string
  compact?: boolean
}

export default function InstagramPostMaker({
  article,
  open,
  onClose,
  triggerClassName,
  compact = false,
}: InstagramPostMakerProps) {
  const [internalOpen, setInternalOpen] = useState(false)
  const isOpen = open ?? internalOpen
  const [previewUrl, setPreviewUrl] = useState('')
  const [generating, setGenerating] = useState(false)
  const [copied, setCopied] = useState(false)
  const [logoUrl, setLogoUrl] = useState('')
  const [siteName, setSiteName] = useState('CARLOS FELIPE')
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const previewUrlRef = useRef('')
  const pngBlobRef = useRef<Blob | null>(null)

  const slug = article.slug || article._id
  const articleUrl = `${SITE_URL}/artigo/${encodeURIComponent(slug)}`

  useEffect(() => {
    let cancelled = false
    Promise.all([
      lumi.entities.site_settings.list({ filter: { key: 'logo_image' }, limit: 1 }),
      lumi.entities.site_settings.list({ filter: { key: 'site_title' }, limit: 1 }),
    ]).then(([logoRes, titleRes]) => {
      if (cancelled) return
      const logo = (logoRes.list?.[0] as SiteSetting | undefined)?.value || ''
      const title = (titleRes.list?.[0] as SiteSetting | undefined)?.value || ''
      setLogoUrl(logo)
      setSiteName(title.trim() || 'CARLOS FELIPE')
    }).catch(() => {
      /* silencioso */
    })
    return () => { cancelled = true }
  }, [])

  useEffect(() => {
    if (!isOpen) return
    let cancelled = false
    const generate = async () => {
      setGenerating(true)
      pngBlobRef.current = null
      try {
        const canvas = createArticlePosterCanvas()
        canvasRef.current = canvas
        const posterData = {
          title: article.title,
          authorName: article.authorName || 'CARLOS FELIPE',
          dateIso: article.publishedAt || article.createdAt || new Date().toISOString(),
          imageUrl: article.imageUrl || article.ogImage || article.galleryImages?.[0] || '',
          extraImageUrls: [
            article.ogImage || '',
            ...(article.galleryImages || []),
          ].filter(Boolean),
          logoUrl,
          siteName,
          category: article.category || '',
          excerpt: excerptFromArticle(article.excerpt, article.content),
        }
        await drawArticlePoster(canvas, posterData)
        if (cancelled) return
        const blob = await canvasToPngBlob(canvas)
        if (cancelled) return
        pngBlobRef.current = blob
        if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current)
        const nextUrl = URL.createObjectURL(blob)
        previewUrlRef.current = nextUrl
        setPreviewUrl(nextUrl)
      } catch (err) {
        console.error('Erro ao gerar post Instagram:', err)
        toast.error('Não foi possível gerar a imagem. Tente novamente.')
      } finally {
        if (!cancelled) setGenerating(false)
      }
    }
    generate()
    return () => {
      cancelled = true
    }
  }, [
    isOpen,
    article._id,
    article.title,
    article.authorName,
    article.publishedAt,
    article.createdAt,
    article.imageUrl,
    article.ogImage,
    article.category,
    article.excerpt,
    article.content,
    logoUrl,
    siteName,
  ])

  useEffect(() => {
    return () => {
      if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current)
    }
  }, [])

  const close = () => {
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current)
      previewUrlRef.current = ''
    }
    pngBlobRef.current = null
    setPreviewUrl('')
    setInternalOpen(false)
    onClose?.()
  }

  const handleDownload = () => {
    const filename = `carlos-felipe-${slugifyFilename(article.title)}.png`
    const blob = pngBlobRef.current
    if (blob) {
      downloadPngBlob(blob, filename)
      toast.success('Imagem descarregada. Já pode publicar no Instagram.')
      return
    }
    const canvas = canvasRef.current
    if (!canvas) {
      toast.error('A imagem ainda está a ser gerada.')
      return
    }
    downloadCanvasPng(canvas, filename)
    toast.success('Imagem descarregada. Já pode publicar no Instagram.')
  }

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(articleUrl)
      setCopied(true)
      toast.success('Link da matéria copiado.')
      window.setTimeout(() => setCopied(false), 2500)
    } catch {
      toast.error('Não foi possível copiar o link.')
    }
  }

  const modal = isOpen ? createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 px-4 py-6"
      onClick={close}
    >
      <div
        className="relative w-full max-w-lg overflow-hidden rounded-2xl bg-white shadow-2xl dark:bg-gray-950"
        onClick={e => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="ig-post-title"
      >
        <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4 dark:border-gray-800">
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-red-600">
              CARLOS FELIPE
            </p>
            <h3 id="ig-post-title" className="mt-0.5 text-base font-semibold text-gray-900 dark:text-white">
              Post para Instagram
            </h3>
          </div>
          <button
            type="button"
            onClick={close}
            className="rounded-lg p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-gray-800"
            aria-label="Fechar"
          >
            <X size={18} />
          </button>
        </div>

        <div className="px-5 py-4">
          <div className="overflow-hidden rounded-xl border border-gray-100 bg-gray-50 dark:border-gray-800 dark:bg-gray-900">
            {generating ? (
              <div className="flex aspect-[4/5] items-center justify-center gap-2 text-sm text-gray-400">
                <Loader2 size={18} className="animate-spin text-red-600" />
                A gerar imagem…
              </div>
            ) : previewUrl ? (
              <img
                src={previewUrl}
                alt={`Pré-visualização Instagram: ${article.title}`}
                className="block w-full"
              />
            ) : (
              <div className="flex aspect-[4/5] items-center justify-center px-6 text-center text-sm text-gray-400">
                Sem pré-visualização disponível.
              </div>
            )}
          </div>

          <p className="mt-3 text-xs leading-relaxed text-gray-500 dark:text-gray-400">
            Formato de feed (1080 x 1350, 4:5) com foto, data e autor, título e a logo no canto inferior direito. Copie o link para colar na descrição do Instagram.
          </p>

          <div className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-2">
            <button
              type="button"
              onClick={handleDownload}
              disabled={generating || !previewUrl}
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-red-600 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-red-700 disabled:opacity-50"
            >
              <Download size={15} />
              Descarregar imagem
            </button>
            <button
              type="button"
              onClick={handleCopy}
              className={`inline-flex items-center justify-center gap-2 rounded-xl border px-4 py-2.5 text-sm font-semibold transition-colors ${
                copied
                  ? 'border-green-300 bg-green-50 text-green-700 dark:border-green-800 dark:bg-green-950/40 dark:text-green-400'
                  : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-200'
              }`}
            >
              {copied ? <Check size={15} /> : <Link2 size={15} />}
              {copied ? 'Link copiado' : 'Copiar link'}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  ) : null

  if (open !== undefined) return modal

  return (
    <>
      <button
        type="button"
        onClick={() => setInternalOpen(true)}
        className={
          triggerClassName ||
          'inline-flex items-center gap-1.5 rounded-lg bg-[#E1306C] px-3 py-1.5 text-xs font-semibold text-white shadow-sm transition-all hover:bg-[#c2255c] active:scale-95'
        }
        aria-label="Criar imagem para Instagram"
        title="Criar imagem para Instagram"
      >
        <Instagram size={compact ? 15 : 13} />
        {compact ? null : <span>Instagram</span>}
      </button>
      {modal}
    </>
  )
}
